import React, { useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
} from 'react-router-dom';
import './App.css';

const vignettes = [
  {
    title: 'The Legacy Circle',
    text: 'A family sought renewed structure and meaning after loss. Companionship centered on conversation, faith, retreats, and preserving stories—bringing peace and purpose to daily life.',
  },
  {
    title: 'The Cultural Patron',
    text: 'A retired academic desired continued engagement with the arts. Private performances, gallery access, and curated discussions kept cultural immersion effortless and vibrant.',
  },
  {
    title: 'The Global Voyager',
    text: 'An international traveler wanted to continue long stays abroad. Discreet, consistent companionship made overseas life seamless—routine, exploration, and family reassurance across time zones.',
  },
  {
    title: 'The Chairman’s Circle',
    text: 'A family office engaged continuous presence for a principal needing absolute discretion. With founder oversight and on-demand availability, travel and private engagements ran without friction.',
  },
];

function HomePage() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [contactMethod, setContactMethod] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const openDialog = () => {
    setContactMethod('');
    setMessage('');
    setSubmitted(false);
    setIsDialogOpen(true);
  };

  const closeDialog = () => setIsDialogOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Try to get API URL from environment variable
    // For local dev: use .env.local file
    // For production: set during build in GitHub Actions
    const apiUrl = process.env.REACT_APP_API_URL || '';
    if (!apiUrl) {
      console.error('REACT_APP_API_URL is not set');
      alert('API URL not configured. Please contact support at info@characompanionship.com');
      return;
    }
    
    try {
      const response = await fetch(`${apiUrl}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contactMethod: contactMethod,
          message: message,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          setIsDialogOpen(false);
        }, 2000);
      } else {
        alert(data.error || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Network error. Please check your connection and try again.');
    }
  };

  return (
    <div className="page">
      <header id="home" className="hero">
        <img src="/cc_logo.png" alt="Chara Companionship Logo" className="hero-logo" />
        {/* Option 1: Chara Companionship - CENTERED */}
        <h1 className="hero-title">
          Chara Companionship
        </h1>
        {/* Option 2: Centered (uncomment to test)
        <h1 className="hero-title centered">
          CHARA Companionship
        </h1>
        */}
        {/* Option 3: Standard styling, just text change (uncomment to test)
        <h1 className="hero-title">
          CHARA Companionship
        </h1>
        */}
        <p className="lede">
          Bespoke, white-glove companionship for discerning seniors and families who value dignity, discretion, and meaningful connection.
        </p>
        <div style={{ textAlign: 'center', marginTop: '30px' }}>
          <button className="cta filled" onClick={openDialog}>Request a Private Consultation</button>
        </div>
      </header>

      <section id="philosophy" className="section">
        <h2>Our Philosophy</h2>
        <p>We believe aging should be honored — never reduced to tasks or transactions. Chara Companionship exists to provide intentional presence, intellectual engagement, and refined experiences that elevate daily life.</p>
        <p className="small">Founder-led. Faith-informed. Purpose-driven.</p>
      </section>

      <section id="vignettes" className="section">
        <h2>Private Client Vignettes</h2>
        <p className="small">Fictionalized composites to illustrate our work. Identities are always protected.</p>
        <div className="cards">
          {vignettes.map((vignette) => (
            <div className="card" key={vignette.title}>
              <h3>{vignette.title}</h3>
              <p>{vignette.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="founder-letter" className="section">
        <h2>A Letter from the Founder</h2>
        <p>
          Chara Companionship was founded on a simple belief: that presence, when offered with intention and respect, has the power to restore dignity, meaning, and peace of mind.
        </p>
        <p>
          I have spent my career working alongside families, professionals, and institutions where trust is not assumed — it is earned. This firm was built to serve those who value discretion, continuity, and human connection at the highest level.
        </p>
        <p>
          We do not measure our work in hours alone. We measure it in confidence restored, relationships strengthened, and lives honored well.
        </p>
        <p className="small"><strong>Christian Chara</strong><br />Founder &amp; Managing Director</p>
      </section>

      <section id="contact" className="section">
        <h2>Begin the Conversation</h2>
        <p>Private consultations are available for families seeking an elevated approach to companionship.</p>
        <div style={{ textAlign: 'center', marginTop: '30px' }}>
          <button className="cta filled" onClick={openDialog}>Request a Private Consultation</button>
        </div>
      </section>

      <footer className="footer">
        © Chara Companionship · All Rights Reserved
        <br />
        <span className="small">A private, non-medical companionship concierge.</span>
      </footer>

      {isDialogOpen && (
        <div className="dialog-backdrop" onClick={closeDialog}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Request a Private Consultation</h3>
            {submitted ? (
              <p className="success">Thank you. We’ll be in touch shortly.</p>
            ) : (
              <form onSubmit={handleSubmit} className="form">
                <label>
                  Preferred contact (email or phone)
                  <input
                    type="text"
                    value={contactMethod}
                    onChange={(e) => setContactMethod(e.target.value)}
                    placeholder="name@example.com or (555) 555-5555"
                    required
                  />
                </label>
                <label>
                  Message
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Share what you’re looking for or any questions you have."
                    rows={4}
                    required
                  />
                </label>
                <div className="dialog-actions">
                  <button type="button" className="ghost" onClick={closeDialog}>Cancel</button>
                  <button type="submit" className="cta small-btn">Send</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <div className="page">
        <nav className="nav">
          <div className="nav-left">
            <img src="/cc_logo.png" alt="Chara Companionship Logo" className="logo" />
          </div>
          <div className="nav-links">
            <Link to="/">Home</Link>
            <Link to="/companions">Our Companions</Link>
            <Link to="/faq">FAQ</Link>
          </div>
        </nav>

        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route
              path="/companions"
              element={
                <div className="page">
                  <section className="section" style={{ paddingTop: '140px' }}>
                    <h2>Our Companions</h2>
                    <p>Our Companions are carefully selected professionals — highly educated, discreet, and emotionally intelligent. They are trained in etiquette, confidentiality, safety, and aging awareness.</p>
                    <p>We do not staff for volume. We assign for compatibility.</p>
                  </section>
                </div>
              }
            />
            <Route
              path="/faq"
              element={
                <div className="page">
                  <section className="section" style={{ paddingTop: '140px' }}>
                    <h2>Frequently Asked Questions</h2>
                    <p>
                      <strong>Is this medical care?</strong><br />
                      No. Chara Companionship is a non-medical, private-pay companionship service.
                    </p>
                    <p>
                      <strong>How many clients do you serve?</strong><br />
                      We intentionally limit our client base to preserve quality, discretion, and continuity.
                    </p>
                  </section>
                </div>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;

